"use client";

import { Mic, MicOff, MonitorUp, Video, VideoOff } from "lucide-react";

export type AssignmentMode = "manual" | "auto" | "self_select";

/** LiveKit track snapshot from breakout status API (Track.Source values). */
export type BreakoutLiveTrack = { type: number; source: number; muted: boolean };

export function BreakoutParticipantMediaIcons({
  tracks,
  className = ""
}: {
  tracks: BreakoutLiveTrack[];
  className?: string;
}) {
  const audioTrack = tracks.find((track) => track.source === 2);
  const videoTrack = tracks.find((track) => track.source === 1);
  const sharing = tracks.some((track) => track.source === 3);
  const micOn = Boolean(audioTrack && !audioTrack.muted);
  const camOn = Boolean(videoTrack && !videoTrack.muted);

  return (
    <div className={`flex items-center gap-2 ${className}`} aria-label="Media status">
      {micOn ? (
        <Mic className="size-3.5 text-green-600" aria-hidden />
      ) : (
        <MicOff className="size-3.5 text-gray-400" aria-hidden />
      )}
      {camOn ? (
        <Video className="size-3.5 text-blue-600" aria-hidden />
      ) : (
        <VideoOff className="size-3.5 text-gray-400" aria-hidden />
      )}
      {sharing ? <MonitorUp className="size-3.5 text-amber-600" aria-hidden /> : null}
    </div>
  );
}

const dialogFocusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

export const dialogButtonPrimary = `min-h-11 min-w-[7.5rem] cursor-pointer rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50 ${dialogFocusRing}`;

export const dialogButtonSecondary = `min-h-11 cursor-pointer rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${dialogFocusRing}`;

export const dialogIconButton = `inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 ${dialogFocusRing}`;

export const dialogNumberInput = `w-16 min-h-11 rounded-lg border border-gray-300 bg-white px-2 py-2 text-center text-sm font-medium text-gray-900 tabular-nums shadow-sm transition-colors hover:border-gray-400 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70`;

export const dialogFieldInput = `min-h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors hover:border-gray-400 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70`;

const ASSIGNMENT_OPTIONS: Array<{ value: AssignmentMode; label: string; description: string }> = [
  {
    value: "auto",
    label: "Assign automatically",
    description: "Split people evenly across rooms when breakout rooms open."
  },
  {
    value: "manual",
    label: "Assign manually",
    description: "Choose a room for each person before opening breakout rooms."
  },
  {
    value: "self_select",
    label: "Let participants choose room",
    description: "Participants pick a room after breakout rooms open."
  }
];

export function BreakoutAssignmentModeFieldset({
  name,
  value,
  onChange,
  disabled
}: {
  name: string;
  value: AssignmentMode;
  onChange: (mode: AssignmentMode) => void;
  disabled?: boolean;
}) {
  return (
    <fieldset className="space-y-1" disabled={disabled}>
      <legend className="sr-only">Assignment method</legend>
      {ASSIGNMENT_OPTIONS.map((option) => {
        const checked = value === option.value;
        return (
          <label
            key={option.value}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition-colors ${
              checked ? "border-primary/35 bg-gray-50 ring-1 ring-primary/15" : "border-transparent hover:bg-gray-50"
            } has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/30 ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
            onClick={(event) => {
              if (disabled) {
                event.preventDefault();
                return;
              }
              if (value !== option.value) onChange(option.value);
            }}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={checked}
              onChange={() => onChange(option.value)}
              disabled={disabled}
              className="peer sr-only"
            />
            <span
              aria-hidden
              className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 bg-white transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-2 ${
                checked ? "border-primary" : "border-gray-400"
              }`}
            >
              {checked ? <span className="size-2 rounded-full bg-primary" /> : null}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">{option.label}</span>
              <span className="mt-0.5 block text-sm leading-snug text-gray-600">{option.description}</span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
